import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header';
import ChatWindow from './components/ChatWindow';
import InputBar from './components/InputBar';
import MenuPanel from './components/MenuPanel';
import SettingsPanel from './components/SettingsPanel';
import { sendMessageToAI } from '../../services/chatService';
import type { Message } from '../../types/chat';
import { useTheme } from '../../contexts/ThemeContext';

export default function HomePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const handleSendMessage = async (content: string) => {
    // Agregar mensaje del usuario
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Mostrar estado "escribiendo"
    setIsTyping(true);

    try {
      // Preparar historial de conversación para la IA
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Enviar mensaje a la IA
      const response = await sendMessageToAI(content, conversationHistory);
      
      // Si la respuesta no fue exitosa, navegar a página de error
      if (!response.success) {
        setIsTyping(false);
        
        // Determinar tipo de error basado en el mensaje
        let errorType: 'connection' | 'session' | 'server' | 'unknown' = 'unknown';
        
        if (response.message.includes('Failed to fetch') || response.message.includes('NetworkError') || response.message.includes('No se pudo conectar')) {
          errorType = 'connection';
        } else if (response.message.includes('404')) {
          errorType = 'server';
        } else if (response.message.includes('500')) {
          errorType = 'server';
        }
        
        navigate('/error', {
          state: {
            type: errorType,
            message: response.message
          }
        });
        return;
      }
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);

      // Si completamos todos los pasos, navegar a resultado después de un breve delay
      /*
      if (newStep === totalSteps) {
        setTimeout(() => {
          navigate('/result', {
            state: {
              projectData: {
                cost: '$5,000 - $8,000',
                time: '4-6 semanas',
                scope: 'Aplicación móvil completa',
                summary: 'Tu proyecto es una aplicación de chat con IA que incluye múltiples temas, progreso de conversación y experiencia de usuario optimizada.',
                details: [
                  'Interfaz de usuario moderna y responsive',
                  'Sistema de temas claro/oscuro',
                  'Integración con IA conversacional',
                  'Indicador de progreso por pasos',
                  'Animaciones y transiciones suaves',
                  'Pantalla de inicio personalizada'
                ]
              }
            }
          });
        }, 1500);
      }
        */
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      setIsTyping(false);
      
      // Navegar a página de error en caso de excepción
      navigate('/error', {
        state: {
          type: 'unknown',
          message: 'Ocurrió un error inesperado al procesar tu mensaje.'
        }
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setIsMenuOpen(false);
  };

  return (
    <div className={`h-screen flex flex-col ${
      theme === 'dark'
        ? 'bg-gradient-to-br from-[#0A0118] via-[#1A0B2E] to-[#0A0118]'
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>
      {/* Efectos de fondo */}
      {theme === 'dark' && (
        <>
          <div className="fixed top-20 left-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="fixed bottom-20 right-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </>
      )}

      {/* Header */}
      <Header
        onMenuClick={() => setIsMenuOpen(true)}
        onSettingsClick={() => setIsSettingsOpen(true)}
        onNewChat={handleNewChat}
      />

      {/* Área de mensajes */}
      <div className="flex-1 overflow-hidden pt-14 pb-28">
        <div className="max-w-md mx-auto h-full">
          <ChatWindow messages={messages} isTyping={isTyping} />
        </div>
      </div>

      {/* Input fijo abajo */}
      <InputBar onSendMessage={handleSendMessage} disabled={isTyping} />

      {/* Paneles laterales */}
      <MenuPanel
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onNewChat={handleNewChat}
      />
      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </div>
  );
}
